import { ChartNode } from "@/components/chart/ChartNode.tsx";
import { Button, Stack, FontAwesomeIcon } from "@skailer-com/uikit";
import { HierarchyNode, select } from "d3";
import { OrgChart } from "d3-org-chart";
import { ReactElement, useEffect, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useTranslation } from "react-i18next";

export interface ChartNodeData<T> {
  id: string;
  parentId: string | null;
  title: string;
  caption?: string;
  extra?: string;
  imgUrl?: string | null;
  avatarElement?: ReactElement;
  count: number;
  isRoot: boolean;
  isExpandUpNode: boolean;
  original?: T;
}

export interface ChartProps<T> {
  data: ChartNodeData<T>[];
  expandedSet?: Set<string>;
  onNodeClick?: (nodeId: string) => void;
  onExpandUpClick?: (nodeId: string) => void;
  onExpandClick?: (nodeId: string, needLoadData: boolean) => void;
}

type ChartLayoutNode<T> = HierarchyNode<ChartNodeData<T>> & {
  id?: string;
  x0?: number;
  y0?: number;
  width?: number;
};

export function Chart<T>({
  data,
  onNodeClick,
  onExpandClick,
  onExpandUpClick,
  expandedSet,
}: ChartProps<T>) {
  const d3Container = useRef<HTMLDivElement>(null);
  const hasRenderedRef = useRef(false);
  const transitionSourceIdRef = useRef<string | null>(null);
  const pendingLoadSourceIdRef = useRef<string | null>(null);
  const dataRef = useRef(data);
  const chartDataSignatureRef = useRef<string | null>(null);
  const chartDataIdsRef = useRef<Set<string>>(new Set());
  const [chart] = useState(() => new OrgChart<ChartNodeData<T>>());
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set(expandedSet ?? []));
  const [syncedExpandedSet, setSyncedExpandedSet] = useState(expandedSet);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set([]));
  const [rootCollapsed, setRootCollapsed] = useState<boolean>(false);
  const { t } = useTranslation();

  if (expandedSet !== syncedExpandedSet) {
    setSyncedExpandedSet(expandedSet);
    if (expandedSet) setExpanded(new Set(expandedSet));
  }

  useEffect(() => {
    if (!d3Container.current) return;

    const container = d3Container.current;

    const resizeObserver = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;

      const svg = container.querySelector("svg");
      if (svg) {
        svg.setAttribute("width", `${entry.contentRect.width}`);
        svg.setAttribute("height", `${entry.contentRect.height}`);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  const getChartDataSignature = (chartData: Array<ChartNodeData<T> & { _expanded: boolean }>) => {
    return chartData
      .map((item) => `${item.id}:${item.parentId ?? ""}`)
      .sort()
      .join("|");
  };

  const getAddedNodesParentId = (addedNodes: ChartNodeData<T>[]) => {
    const parentIds = new Set(
      addedNodes
        .map((item) => item.parentId)
        .filter((parentId): parentId is string => Boolean(parentId))
    );

    return parentIds.size === 1 ? [...parentIds][0] : null;
  };

  const getTransitionSourceNode = (
    chartInstance: OrgChart<ChartNodeData<T>>,
    fallbackSourceId: string | null
  ) => {
    const state = chartInstance.getChartState();
    const sourceId = transitionSourceIdRef.current ?? fallbackSourceId;
    const sourceNode = (
      sourceId ? state.allNodes?.find((node) => node.id === sourceId) : state.root
    ) as ChartLayoutNode<T> | undefined;

    if (!sourceNode) return null;

    return {
      x0: sourceNode.x0 ?? sourceNode.x ?? 0,
      y0: sourceNode.y0 ?? sourceNode.y ?? 0,
      x: sourceNode.x ?? sourceNode.x0 ?? 0,
      y: sourceNode.y ?? sourceNode.y0 ?? 0,
      width: sourceNode.width ?? 0,
      height: sourceNode.height ?? 0,
    };
  };

  const renderExpandUpButton = (nodeData: ChartNodeData<T>) => {
    return nodeData.isExpandUpNode ? (
      <Stack className={"items-center cursor-default"}>
        <Button
          variant={"secondary"}
          className={"chart-expand-up-button"}
          data-id={nodeData.id}
          rightIcon={<FontAwesomeIcon icon="fa-light fa-chevron-up" />}
        >
          {t("core:to_up")}
        </Button>
      </Stack>
    ) : (
      <ChartNode expanded={isNodeExpanded(nodeData)} data={nodeData} />
    );
  };

  const isNodeExpanded = (nodeData: ChartNodeData<T>) => {
    if (nodeData.isRoot) return !rootCollapsed;

    return expanded.has(nodeData.id) && !collapsed.has(nodeData.id);
  };

  const toggleEvents = (isBindMode: boolean = false) => {
    if (isBindMode) {
      d3Container.current
        ?.querySelectorAll(".structure-item .chart-expand-button")
        .forEach((button) => {
          if (button instanceof HTMLElement) button.addEventListener("click", clickOnExpand);
        });

      d3Container.current?.querySelectorAll(".chart-expand-up-button").forEach((button) => {
        if (button instanceof HTMLElement) button.addEventListener("click", clickOnExpandUp);
      });
    } else {
      d3Container.current
        ?.querySelectorAll(".structure-item .chart-expand-button")
        .forEach((button) => {
          if (button instanceof HTMLElement) button.removeEventListener("click", clickOnExpand);
        });

      d3Container.current?.querySelectorAll(".chart-expand-up-button").forEach((button) => {
        if (button instanceof HTMLElement) button.removeEventListener("click", clickOnExpandUp);
      });
    }
  };

  const updateDOMElementsAfterRender = () => {
    const rootNode = document.querySelector("#structure-node__root");

    if (rootNode) updateDOMElement(rootNode, rootCollapsed);

    expanded.forEach((id) => {
      const node = document.querySelector("#structure-node__" + id);

      if (node) updateDOMElement(node, collapsed.has(id));
    });
  };

  const updateDOMElement = (element: Element, isCollapsed: boolean) => {
    const btn = element.querySelector(".chart-expand-button");
    const icon = btn?.querySelector(".fa-light");

    if (isCollapsed) {
      element.classList.remove("border-dark", "hover:border-dark");
      element.classList.add("border-transparent", "hover:border-dark/10");

      btn?.classList.remove("bg-brand-dark", "text-light");
      btn?.classList.add("bg-brand-dark/5", "text-brand-dark", "hover:bg-brand-dark/10");

      icon?.classList.remove("fa-chevron-down");
      icon?.classList.add("fa-chevron-right");
    } else {
      element.classList.remove("hover:border-dark/10", "border-transparent");
      element.classList.add("border-dark", "hover:border-dark");

      btn?.classList.add("bg-brand-dark", "text-light");
      btn?.classList.remove("bg-brand-dark/5", "text-brand-dark", "hover:bg-brand-dark/10");

      icon?.classList.add("fa-chevron-down");
      icon?.classList.remove("fa-chevron-right");
    }
  };

  const clickOnExpandUp = (event: MouseEvent) => {
    const target = event.currentTarget as HTMLElement;
    const dataId = target.getAttribute("data-id");

    if (!dataId) return;

    event.stopPropagation();
    event.stopImmediatePropagation();

    transitionSourceIdRef.current = dataId;
    pendingLoadSourceIdRef.current = dataId;

    if (onExpandUpClick) onExpandUpClick(dataId);
  };

  const clickOnExpand = (event: MouseEvent) => {
    const target = event.currentTarget as HTMLElement;
    const dataId = target.getAttribute("data-id");
    let needLoadBatch = false;

    event.stopPropagation();
    event.stopImmediatePropagation();

    if (!dataId) return;

    transitionSourceIdRef.current = dataId;

    if (dataId == "root") {
      needLoadBatch = false;

      setRootCollapsed((prevState) => !prevState);
    } else {
      const newCollapsed = new Set(collapsed);
      const newExpanded = new Set(expanded);

      if (!expanded.has(dataId)) {
        needLoadBatch = true;
        pendingLoadSourceIdRef.current = dataId;

        newCollapsed.delete(dataId);
        newExpanded.add(dataId);
      } else if (collapsed.has(dataId)) {
        newCollapsed.delete(dataId);
      } else if (expanded.has(dataId) && !collapsed.has(dataId)) {
        newCollapsed.add(dataId);

        getChildrenIds(dataId).forEach((id) => {
          newCollapsed.add(id);
        });
      }

      setExpanded(newExpanded);
      setCollapsed(newCollapsed);
    }

    if (onExpandClick) onExpandClick(dataId, needLoadBatch);
  };

  const getChildrenIds = function getChildrenIds(targetId: string): string[] {
    const childrenIds: string[] = [];

    function findChildren(parentId: string) {
      for (const node of data) {
        if (node.parentId != parentId) continue;

        childrenIds.push(node.id);

        findChildren(node.id);
      }
    }

    findChildren(targetId);

    return childrenIds;
  };

  useEffect(() => {
    if (!d3Container.current) return;

    toggleEvents();

    const chartData = data
      .filter((item) => {
        const isCollapsed = !collapsed.has(item.parentId ?? "") && !rootCollapsed;

        return isCollapsed || item.isRoot;
      })
      .map((item) => ({
        ...item,
        _expanded: true,
      }));

    const chartDataSignature = getChartDataSignature(chartData);
    const isDataChanged = dataRef.current !== data;
    const isChartDataChanged =
      chartDataSignatureRef.current !== null &&
      chartDataSignatureRef.current !== chartDataSignature;
    const addedNodes = chartData.filter((item) => !chartDataIdsRef.current.has(item.id));
    const sourceNode = getTransitionSourceNode(chart, getAddedNodesParentId(addedNodes));

    chart
      .container(d3Container.current as unknown as string)
      .svgHeight(d3Container.current.getBoundingClientRect().height)
      .data(chartData)
      .linkUpdate((_node, _index, nodes) => {
        nodes.forEach((nodeChild) => {
          select(nodeChild as unknown as SVGPathElement).attr(
            "stroke",
            "color-mix(in srgb, var(--color-dark) 20%, transparent)"
          );
        });
      })
      .compact(false)
      .duration(100)
      .nodeWidth(() => 164)
      .nodeHeight((node) => {
        if (node.data.isExpandUpNode) return 24;

        return node.data.count > 0 ? 198 : 168;
      })
      .buttonContent(() => "")
      .nodeButtonHeight(() => 0)
      .nodeContent((node) => {
        return renderToStaticMarkup(renderExpandUpButton(node.data));
      })
      .onNodeClick((node: HierarchyNode<ChartNodeData<T>>) => {
        if (node.data.isExpandUpNode) return;

        if (onNodeClick && node.id) onNodeClick(node.id);
      });

    if (hasRenderedRef.current && sourceNode) {
      chart.setLayouts({ expandNodesFirst: false });
      chart.update(sourceNode);
    } else {
      chart.render();
      hasRenderedRef.current = true;
    }

    toggleEvents(true);

    updateDOMElementsAfterRender();
    dataRef.current = data;
    chartDataSignatureRef.current = chartDataSignature;
    chartDataIdsRef.current = new Set(chartData.map((item) => item.id));

    if (!pendingLoadSourceIdRef.current || isDataChanged || isChartDataChanged) {
      transitionSourceIdRef.current = null;
      pendingLoadSourceIdRef.current = null;
    }
  }, [data, chart, collapsed, rootCollapsed]);

  return <div className={"h-full"} ref={d3Container} />;
}
